import { getSupabase, isSupabaseConfigured } from '../lib/supabase';
import { assertOnlineOrThrow } from '../lib/offlineGuard';

/** @param {string} ownerUserId */
export async function countUnreadBusinessInquiries(ownerUserId) {
  if (!isSupabaseConfigured || !ownerUserId) return 0;
  const sb = getSupabase();
  if (!sb) return 0;
  try {
    const { count, error } = await sb
      .from('business_inquiries')
      .select('id', { count: 'exact', head: true })
      .eq('owner_id', ownerUserId)
      .eq('status', 'pending');
    if (error) return 0;
    return count ?? 0;
  } catch {
    return 0;
  }
}

/**
 * @param {string} ownerUserId
 * @param {{ tab: string; businessId?: string }} opts tab: all|unread|booking|general|done
 */
export async function fetchBusinessInquiriesForOwner(ownerUserId, opts = {}) {
  if (!isSupabaseConfigured || !ownerUserId) return [];
  const sb = getSupabase();
  if (!sb) return [];
  assertOnlineOrThrow();
  let q = sb.from('business_inquiries').select('*').eq('owner_id', ownerUserId).order('created_at', { ascending: false }).limit(200);
  const { tab = 'all', businessId = '' } = opts;
  if (tab === 'unread') q = q.eq('status', 'pending');
  else if (tab === 'booking') q = q.eq('inquiry_type', 'booking');
  else if (tab === 'general') q = q.eq('inquiry_type', 'general');
  else if (tab === 'done') q = q.eq('status', 'closed');
  if (businessId) q = q.eq('business_id', businessId);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

/** @param {string[]} userIds */
export async function fetchUsersPublicMini(userIds) {
  if (!isSupabaseConfigured || !userIds?.length) return {};
  const sb = getSupabase();
  if (!sb) return {};
  const uniq = [...new Set(userIds.filter(Boolean))];
  if (!uniq.length) return {};
  const { data, error } = await sb
    .from('users')
    .select('id, nickname, avatar_url, manner_temperature, manner_signal_count')
    .in('id', uniq);
  if (error) return {};
  const map = {};
  (data || []).forEach((u) => {
    map[u.id] = u;
  });
  return map;
}

/**
 * @param {string} businessId
 * @param {string} ownerUserId
 */
export async function fetchBusinessContactById(businessId) {
  if (!isSupabaseConfigured || !businessId) return '';
  const sb = getSupabase();
  if (!sb) return '';
  const { data } = await sb.from('businesses').select('contact').eq('id', businessId).maybeSingle();
  return (data && data.contact) || '';
}

/**
 * @param {{ businessId: string; ownerId: string | null; senderId: string; inquiryType: 'booking'|'general'|'quote'; message: string }} p
 */
export async function submitBusinessInquiry(p) {
  const sb = getSupabase();
  if (!sb || !isSupabaseConfigured) {
    const e = new Error('NO_SUPABASE');
    e.code = 'NO_SUPABASE';
    throw e;
  }
  assertOnlineOrThrow();
  const payload = {
    business_id: p.businessId,
    owner_id: p.ownerId || null,
    sender_id: p.senderId,
    inquiry_type: p.inquiryType || 'general',
    status: 'pending',
    message: (p.message || '').trim() || '(내용 없음)',
  };
  const { data, error } = await sb.from('business_inquiries').insert(payload).select('id').maybeSingle();
  if (error) throw error;
  return data?.id || null;
}

/** First active business id for owner (hotel/guide fallback routing). */
export async function fetchFirstBusinessIdForOwner(ownerUserId) {
  if (!isSupabaseConfigured || !ownerUserId) return null;
  const sb = getSupabase();
  if (!sb) return null;
  const { data } = await sb
    .from('businesses')
    .select('id')
    .eq('owner_user_id', ownerUserId)
    .eq('active', true)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  return data?.id || null;
}

/**
 * @param {string} inquiryId
 * @param {string} ownerUserId
 * @param {'pending'|'read'|'closed'} status
 */
export async function updateBusinessInquiryStatus(inquiryId, ownerUserId, status) {
  const sb = getSupabase();
  if (!sb || !isSupabaseConfigured || !inquiryId || !ownerUserId) return { ok: false };
  assertOnlineOrThrow();
  const { error } = await sb
    .from('business_inquiries')
    .update({ status })
    .eq('id', inquiryId)
    .eq('owner_id', ownerUserId);
  if (error) return { ok: false, error };
  return { ok: true };
}

/**
 * @param {string} ownerUserId
 * @param {(row: object) => void} onInsert
 * @returns {() => void} unsubscribe
 */
export function subscribeBusinessInquiries(ownerUserId, onInsert) {
  const sb = getSupabase();
  if (!sb || !ownerUserId || !isSupabaseConfigured) return () => {};
  const channel = sb
    .channel(`business_inquiries:${ownerUserId}`)
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'business_inquiries', filter: `owner_id=eq.${ownerUserId}` },
      (payload) => {
        if (payload.new) onInsert(payload.new);
      },
    )
    .subscribe();
  return () => {
    void sb.removeChannel(channel);
  };
}
